import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import FeatureRenameDialog from "@/v2-backoffice-client/feature/rename/FeatureRenameDialog"
import NavLayerActionMenu from "@/v2-backoffice-client/navlayer/NavLayerActionMenu"
import { useNavLayerQuery } from "@/v2-backoffice-client/navlayer/navlayer.query"
import type { NavLayerFragment } from "@/v2-backoffice-client/navlayer/__types__/navlayer.fragment"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

const COLUMN_SIZING = {
  IsEnabled: 90,
  UpdatedAt: 130,
}

const columns = createColumns<NavLayerFragment>({
  enableSelection: true,
  enableSorting: true,
  enableHiding: true,
  columns: {
    Name: {
      id: "Name",
      label: "Name",
      to: ({ row }) => `?v=navLayer:${row.original.FeatureCode}`,
    },
    Description: {
      id: "Description",
      label: "Description",
      defaultHidden: true,
      renderCell({ row }) {
        const description = row.original.Description
        if (!description) return null
        return (
          <span className="truncate max-w-[200px]" title={description}>
            {description}
          </span>
        )
      },
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature Code",
      filter: { type: "text", placeholder: "Filter by code..." },
    },
    IsEnabled: {
      id: "IsEnabled",
      label: "Status",
      renderCell({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "Enabled" : "Disabled"}
          </DotText>
        )
      },
      exportValue({ value }) {
        return value ? "Enabled" : "Disabled"
      },
    },
    UpdatedAt: {
      id: "UpdatedAt",
      label: "Updated",
      renderCell({ row }) {
        if (!row.original.UpdatedAt) return null
        return formatDateString(String(row.original.UpdatedAt))
      },
      exportValue({ row }) {
        if (!row.original.UpdatedAt) return null
        return formatDateString(String(row.original.UpdatedAt))
      },
    },
  },
})

export type NavLayerTableProps = Omit<
  TableProps<NavLayerFragment>,
  "data" | "columns"
>

/**
 * Every nav layer, disabled ones included. Which countries each one serves
 * is per-feature (common.FeaturesCountries) and lives on the sheet's
 * Countries tab — a column here would be one query per row.
 */
function NavLayerTable(props: NavLayerTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const { data, previousData, loading, error, refetch } = useNavLayerQuery({
    notifyOnNetworkStatusChange: true,
  })

  const displayData = data ?? previousData
  const navLayers = displayData?.getNavLayers ?? []

  // The rename dialog lives up here because the row toolbar that opens it
  // only exists while its row is hovered — a dialog mounted inside it would
  // unmount the moment the pointer leaves for the dialog.
  const [renameTarget, setRenameTarget] = useState<NavLayerFragment | null>(
    null,
  )

  return (
    <>
      <Table
        className={cn("navlayer-table", className)}
        tableId="navlayer-table"
        data={navLayers}
        columns={columns}
        initialColumnSizing={COLUMN_SIZING}
        enableRowSelection
        getHighlightedRowId={(row) => {
          return opener.getIsActive(
            "navLayer",
            String(row.original.FeatureCode),
          )
        }}
        {...tableProps}
      >
        <Table.Toolbar>
          <div className="flex-1" />
          <Table.Search column="Name" placeholder="Search nav layers..." />
          <Separator orientation="vertical" />
          <Table.ToolbarMenu onRefresh={refetch} exportFileName="nav-layers" />
        </Table.Toolbar>

        <Table.FilterList />

        <Table.RowToolbar<NavLayerFragment> column="Name">
          {(navLayer) => (
            <NavLayerActionMenu
              navLayer={navLayer}
              onRename={() => setRenameTarget(navLayer)}
            />
          )}
        </Table.RowToolbar>

        <Table.Content
          countLabel="nav layers"
          isLoading={loading}
          error={error}
        />

        <Table.SelectionToolbar>
          {(navLayers) => (
            <Table.ExportSelectionButton
              fileName="nav-layers"
              selectionCount={navLayers.length}
            />
          )}
        </Table.SelectionToolbar>
      </Table>

      {renameTarget ? (
        <FeatureRenameDialog
          featureCode={renameTarget.FeatureCode}
          name={renameTarget.Name}
          open
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null)
          }}
        />
      ) : null}
    </>
  )
}

export default NavLayerTable
